import { useCallback, useRef } from "react";
import { useGoogleMaps } from "components/map/GoogleMapsProvider";
import { LocationPairRoute } from "_interfaces/driverTripCalculator.interfaces";

interface DirectionsRequest {
  origin: { lat: number; lng: number };
  destination: { lat: number; lng: number };
  avoidTolls: boolean;
}

/**
 * Parse durationMillis from Routes JS SDK response.
 * The JS SDK returns durationMillis as a number in milliseconds.
 */
const parseDurationToMinutes = (durationMillis: number | undefined): number => {
  if (!durationMillis) return 0;
  return Math.round(durationMillis / 60000);
};

/**
 * Hook for Google Maps route calculations using the new Routes API
 * (google.maps.routes.Route.computeRoutes)
 */
export const useGoogleMapsDirections = () => {
  const { isLoaded } = useGoogleMaps();
  const routeClassRef = useRef<any>(null);

  const loadRouteClass = useCallback(async (): Promise<any> => {
    if (routeClassRef.current) return routeClassRef.current;
    if (!window.google?.maps?.importLibrary) return null;

    try {
      const lib = await google.maps.importLibrary("routes");
      routeClassRef.current = (lib as any).Route;
      return routeClassRef.current;
    } catch (e) {
      console.error("Failed to load routes library:", e);
      return null;
    }
  }, []);

  const getDirections = useCallback(
    async (request: DirectionsRequest): Promise<LocationPairRoute | null> => {
      if (!isLoaded || !window.google) return null;

      const Route = await loadRouteClass();
      if (!Route) return null;

      const { origin, destination, avoidTolls } = request;

      const oLat = Number(origin.lat);
      const oLng = Number(origin.lng);
      const dLat = Number(destination.lat);
      const dLng = Number(destination.lng);

      if (!oLat || !oLng || !dLat || !dLng || isNaN(oLat) || isNaN(oLng) || isNaN(dLat) || isNaN(dLng)) {
        return null;
      }

      try {
        const response = await Route.computeRoutes({
          origin: { location: new google.maps.LatLng(oLat, oLng) },
          destination: { location: new google.maps.LatLng(dLat, dLng) },
          travelMode: "DRIVING",
          routeModifiers: { avoidTolls },
          fields: ["legs", "path"],
        });

        const route = response?.routes?.[0];
        const leg = route?.legs?.[0];
        if (!leg) return null;

        return {
          distance_km: (leg.distanceMeters ?? 0) / 1000,
          estimated_time_minutes: parseDurationToMinutes(leg.durationMillis),
          route_polyline: route.path
            ? google.maps.geometry.encoding.encodePath(route.path)
            : undefined,
        };
      } catch (error) {
        console.error("Google Maps Routes API error:", error);
        return null;
      }
    },
    [isLoaded, loadRouteClass],
  );

  // Calculate route for a full segment (multiple waypoints)
  const getSegmentRoute = useCallback(
    async (
      locations: { lat: number; lng: number; avoid_toll: boolean }[],
    ): Promise<{ totalDistance: number; totalTime: number; pairs: LocationPairRoute[] }> => {
      const pairs: LocationPairRoute[] = [];
      let totalDistance = 0;
      let totalTime = 0;

      for (let i = 0; i < locations.length - 1; i++) {
        const route = await getDirections({
          origin: locations[i],
          destination: locations[i + 1],
          avoidTolls: locations[i + 1].avoid_toll,
        });

        if (route) {
          pairs.push(route);
          totalDistance += route.distance_km;
          totalTime += route.estimated_time_minutes;
        }
      }

      return { totalDistance, totalTime, pairs };
    },
    [getDirections],
  );

  return { getDirections, getSegmentRoute, isLoaded };
};
